// src/Features/Projects/models/project.schemas.ts
import * as yup from "yup";
import type { ProjectPhase } from "./project.types";

// Schema para o formulário temporário de criação (antes de expandir com IA)
export const projectDraftSchema = yup.object({
  name: yup
    .string()
    .required("Nome do projeto é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .trim(),

  initialDescription: yup
    .string()
    .required("Descrição inicial é obrigatória")
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(2000, "Descrição deve ter no máximo 2000 caracteres")
    .trim(),

  suggestedName: yup
    .string()
    .optional()
    .max(100, "Nome sugerido deve ter no máximo 100 caracteres")
    .trim(),
});

// Schema para salvar projeto no banco (após processar com IA)
export const createProjectSchema = yup.object({
  name: yup
    .string()
    .required("Nome do projeto é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .trim(),

  description: yup
    .string()
    .required("Descrição é obrigatória")
    .min(50, "Descrição deve ter pelo menos 50 caracteres")
    .max(5000, "Descrição deve ter no máximo 5000 caracteres")
    .trim(),
});

// Schema para expansão de descrição
export const expandDescriptionSchema = yup.object({
  expandedDescription: yup
    .string()
    .required("Descrição expandida é obrigatória")
    .min(50, "Descrição expandida deve ter pelo menos 50 caracteres")
    .max(5000, "Descrição expandida deve ter no máximo 5000 caracteres")
    .trim(),

  suggestedName: yup
    .string()
    .optional()
    .max(100, "Nome sugerido deve ter no máximo 100 caracteres")
    .trim(),
});

// Schema para atualização de projeto
export const updateProjectSchema = yup.object({
  name: yup
    .string()
    .optional()
    .min(3, "Nome deve ter pelo menos 3 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .trim(),

  initialDescription: yup
    .string()
    .optional()
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(2000, "Descrição deve ter no máximo 2000 caracteres")
    .trim(),

  expandedDescription: yup
    .string()
    .optional()
    .max(5000, "Descrição expandida deve ter no máximo 5000 caracteres")
    .trim(),

  status: yup
    .string()
    .optional()
    .oneOf(
      ["draft", "planning", "in_progress", "completed", "cancelled"],
      "Status inválido"
    ),

  currentPhase: yup
    .string()
    .optional()
    .oneOf(
      [
        "description",
        "objectives",
        "technical_solution",
        "milestones",
        "stories",
        "schema",
        "resources",
        "proposal",
        "execution",
      ],
      "Fase inválida"
    ),
});

// Schema para milestone
export const milestoneSchema = yup.object({
  title: yup
    .string()
    .required("Título do marco é obrigatório")
    .min(3, "Título deve ter pelo menos 3 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres")
    .trim(),

  description: yup
    .string()
    .required("Descrição do marco é obrigatória")
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(500, "Descrição deve ter no máximo 500 caracteres")
    .trim(),

  dueDate: yup
    .date()
    .optional()
    .min(new Date(), "Data de entrega deve ser no futuro"),

  order: yup
    .number()
    .required("Ordem é obrigatória")
    .min(1, "Ordem deve ser maior que 0")
    .integer("Ordem deve ser um número inteiro"),
});

// Schema para story
export const storySchema = yup.object({
  title: yup
    .string()
    .required("Título da história é obrigatório")
    .min(3, "Título deve ter pelo menos 3 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres")
    .trim(),

  description: yup
    .string()
    .required("Descrição da história é obrigatória")
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(1000, "Descrição deve ter no máximo 1000 caracteres")
    .trim(),

  acceptanceCriteria: yup
    .array()
    .of(yup.string().required())
    .min(1, "Pelo menos um critério de aceitação é obrigatório")
    .max(10, "Máximo de 10 critérios de aceitação"),

  storyPoints: yup
    .number()
    .optional()
    .min(1, "Story points devem ser maior que 0")
    .max(21, "Story points devem ser no máximo 21")
    .integer("Story points devem ser um número inteiro"),

  priority: yup
    .string()
    .required("Prioridade é obrigatória")
    .oneOf(["low", "medium", "high", "critical"], "Prioridade inválida"),

  order: yup
    .number()
    .required("Ordem é obrigatória")
    .min(1, "Ordem deve ser maior que 0")
    .integer("Ordem deve ser um número inteiro"),
});

// Schema para recurso
export const resourceSchema = yup.object({
  name: yup
    .string()
    .required("Nome do recurso é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(50, "Nome deve ter no máximo 50 caracteres")
    .trim(),

  type: yup
    .string()
    .required("Tipo do recurso é obrigatório")
    .oneOf(
      ["developer", "designer", "pm", "qa", "devops"],
      "Tipo de recurso inválido"
    ),

  hoursEstimated: yup
    .number()
    .required("Horas estimadas são obrigatórias")
    .min(1, "Horas estimadas devem ser maior que 0")
    .max(1000, "Horas estimadas devem ser no máximo 1000"),

  hourlyRate: yup
    .number()
    .required("Taxa horária é obrigatória")
    .min(1, "Taxa horária deve ser maior que 0")
    .max(1000, "Taxa horária deve ser no máximo R$ 1000"),
});

// Schema para edição de projeto (apenas descrição final)
export const editProjectSchema = yup.object({
  name: yup
    .string()
    .required("Nome do projeto é obrigatório")
    .min(3, "Nome deve ter pelo menos 3 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .trim(),

  description: yup
    .string()
    .required("Descrição é obrigatória")
    .min(50, "Descrição deve ter pelo menos 50 caracteres")
    .max(5000, "Descrição deve ter no máximo 5000 caracteres")
    .trim(),
});

// Schema para draft da descrição técnica (antes de expandir com IA)
export const technicalDescriptionDraftSchema = yup.object({
  initialTechnicalDescription: yup
    .string()
    .required("Descrição técnica inicial é obrigatória")
    .min(10, "Descrição técnica deve ter pelo menos 10 caracteres")
    .max(2000, "Descrição técnica deve ter no máximo 2000 caracteres")
    .trim(),
});

// Schema para expansão de descrição técnica
export const expandTechnicalDescriptionSchema = yup.object({
  expandedTechnicalDescription: yup
    .string()
    .required("Descrição técnica expandida é obrigatória")
    .min(50, "Descrição técnica expandida deve ter pelo menos 50 caracteres")
    .max(5000, "Descrição técnica expandida deve ter no máximo 5000 caracteres")
    .trim(),
});

// Schema para solução técnica (Fase 4)
export const techChoiceSchema = yup.object({
  name: yup.string().required("Nome da tecnologia é obrigatório"),
  version: yup.string().optional(),
  justification: yup.string().required("Justificativa é obrigatória"),
  alternatives: yup.array().of(yup.string().required()),
});

export const architectureComponentSchema = yup.object({
  id: yup.string().required("ID do componente é obrigatório"),
  name: yup.string().required("Nome do componente é obrigatório"),
  type: yup
    .string()
    .required("Tipo do componente é obrigatório")
    .oneOf(["frontend", "backend", "database", "external"]),
  description: yup.string().required("Descrição do componente é obrigatória"),
  technologies: yup.array().of(yup.string().required()),
  connections: yup.array().of(yup.string().required()),
});

export const integrationSchema = yup.object({
  id: yup.string().required("ID da integração é obrigatório"),
  name: yup.string().required("Nome da integração é obrigatório"),
  type: yup
    .string()
    .required("Tipo da integração é obrigatório")
    .oneOf(["api", "webhook", "queue", "database", "external"]),
  description: yup.string().required("Descrição da integração é obrigatória"),
  source: yup.string().required("Origem da integração é obrigatória"),
  target: yup.string().required("Destino da integração é obrigatório"),
});

export const technicalSolutionSchema = yup.object({
  description: yup
    .string()
    .required("Descrição técnica é obrigatória")
    .min(50, "Descrição técnica deve ter pelo menos 50 caracteres")
    .max(5000, "Descrição técnica deve ter no máximo 5000 caracteres")
    .trim(),
  recommendedStack: yup.object({
    frontend: yup.array().of(techChoiceSchema),
    backend: yup.array().of(techChoiceSchema),
    database: yup.array().of(techChoiceSchema),
    infrastructure: yup.array().of(techChoiceSchema),
    devops: yup.array().of(techChoiceSchema),
  }),
  architecture: yup.object({
    pattern: yup.string().required("Padrão arquitetural é obrigatório"),
    description: yup.string().required("Descrição da arquitetura é obrigatória"),
    components: yup.array().of(architectureComponentSchema),
    integrations: yup.array().of(integrationSchema),
  }),
  considerations: yup.object({
    performance: yup.array().of(yup.string().required()),
    security: yup.array().of(yup.string().required()),
    scalability: yup.array().of(yup.string().required()),
    maintenance: yup.array().of(yup.string().required()),
  }),
  complexity: yup.object({
    overall: yup
      .string()
      .required("Complexidade geral é obrigatória")
      .oneOf(["low", "medium", "high"]),
    frontend: yup
      .number()
      .required("Complexidade frontend é obrigatória")
      .min(1)
      .max(10),
    backend: yup
      .number()
      .required("Complexidade backend é obrigatória")
      .min(1)
      .max(10),
    devops: yup
      .number()
      .required("Complexidade devops é obrigatória")
      .min(1)
      .max(10),
  }),
  justifications: yup.lazy((obj) =>
    yup.object(
      Object.keys(obj || {}).reduce(
        (acc, key) => ({
          ...acc,
          [key]: yup.string().required(),
        }),
        {}
      )
    )
  ),
});

// Tipos inferidos dos schemas - ATUALIZADO
export type ProjectDraftFormData = yup.InferType<typeof projectDraftSchema>;
export type CreateProjectFormData = yup.InferType<typeof createProjectSchema>;
export type EditProjectFormData = yup.InferType<typeof editProjectSchema>;
export type ExpandDescriptionFormData = yup.InferType<
  typeof expandDescriptionSchema
>;
// Schemas para nova estrutura de milestones
export const newMilestoneSchema = yup.object({
  id: yup.string().required(),
  title: yup.string().required("Título é obrigatório").min(3, "Título deve ter pelo menos 3 caracteres"),
  description: yup.string().required("Descrição é obrigatória").min(10, "Descrição deve ter pelo menos 10 caracteres"),
  estimatedDuration: yup.number().required("Duração estimada é obrigatória").min(1, "Duração deve ser pelo menos 1 dia"),
  dependencies: yup.array().of(yup.string().required()).default([]),
  phase: yup.mixed<ProjectPhase>().required("Fase é obrigatória"),
  deliverables: yup.array().of(yup.string().required()).required().min(1, "Pelo menos um entregável é obrigatório"),
  startDate: yup.string().optional(),
  endDate: yup.string().optional(),
  priority: yup.mixed<"high" | "medium" | "low">().required("Prioridade é obrigatória"),
  status: yup.mixed<"not_started" | "in_progress" | "completed" | "blocked">().required("Status é obrigatório"),
});

export const projectTimelineSchema = yup.object({
  milestones: yup.array().of(newMilestoneSchema).required().min(1, "Pelo menos um milestone é obrigatório"),
  totalDuration: yup.number().required().min(1, "Duração total deve ser pelo menos 1 dia"),
  criticalPath: yup.array().of(yup.string().required()).required(),
  startDate: yup.string().optional(),
  endDate: yup.string().optional(),
});

export const generateMilestonesSchema = yup.object({
  projectId: yup.string().required("ID do projeto é obrigatório"),
  projectDescription: yup.string().required("Descrição do projeto é obrigatória"),
  objectives: yup.array().of(yup.string().required()).required().min(1, "Pelo menos um objetivo é obrigatório"),
  technicalSolution: yup.object().optional(),
  preferredStartDate: yup.string().optional(),
});

export type UpdateProjectFormData = yup.InferType<typeof updateProjectSchema>;
export type MilestoneFormData = yup.InferType<typeof milestoneSchema>;
export type StoryFormData = yup.InferType<typeof storySchema>;
export type ResourceFormData = yup.InferType<typeof resourceSchema>;
export type TechnicalSolutionFormData = yup.InferType<typeof technicalSolutionSchema>;
export type TechChoiceFormData = yup.InferType<typeof techChoiceSchema>;
export type ArchitectureComponentFormData = yup.InferType<typeof architectureComponentSchema>;
export type IntegrationFormData = yup.InferType<typeof integrationSchema>;
export type TechnicalDescriptionDraftFormData = yup.InferType<typeof technicalDescriptionDraftSchema>;
export type ExpandTechnicalDescriptionFormData = yup.InferType<typeof expandTechnicalDescriptionSchema>;

// Novos tipos para milestones
export type NewMilestoneFormData = yup.InferType<typeof newMilestoneSchema>;
export type ProjectTimelineFormData = yup.InferType<typeof projectTimelineSchema>;
export type GenerateMilestonesFormData = yup.InferType<typeof generateMilestonesSchema>;

// ============== USER STORIES SCHEMAS ==============

// Schema para critério de aceitação
export const acceptanceCriteriaSchema = yup.object({
  id: yup.string().required(),
  description: yup
    .string()
    .required("Descrição do critério é obrigatória")
    .min(5, "Descrição deve ter pelo menos 5 caracteres")
    .max(300, "Descrição deve ter no máximo 300 caracteres"),
  completed: yup.boolean().default(false),
});

// Schema para user story
export const userStorySchema = yup.object({
  id: yup.string().required(),
  title: yup
    .string()
    .required("Título é obrigatório")
    .min(5, "Título deve ter pelo menos 5 caracteres")
    .max(100, "Título deve ter no máximo 100 caracteres"),
  description: yup
    .string()
    .required("Descrição é obrigatória")
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(500, "Descrição deve ter no máximo 500 caracteres"),
  
  // Estrutura "Como... Eu quero... Para que..."
  role: yup
    .string()
    .required("Papel do usuário é obrigatório")
    .min(3, "Papel deve ter pelo menos 3 caracteres")
    .max(50, "Papel deve ter no máximo 50 caracteres"),
  functionality: yup
    .string()
    .required("Funcionalidade desejada é obrigatória")
    .min(5, "Funcionalidade deve ter pelo menos 5 caracteres")
    .max(200, "Funcionalidade deve ter no máximo 200 caracteres"),
  benefit: yup
    .string()
    .required("Benefício é obrigatório")
    .min(5, "Benefício deve ter pelo menos 5 caracteres")
    .max(200, "Benefício deve ter no máximo 200 caracteres"),
  
  // Gerenciamento
  priority: yup
    .string()
    .required("Prioridade é obrigatória")
    .oneOf(["must_have", "should_have", "could_have", "wont_have"]),
  status: yup
    .string()
    .required("Status é obrigatório")
    .oneOf(["backlog", "in_progress", "review", "done", "blocked"]),
  type: yup
    .string()
    .required("Tipo é obrigatório")
    .oneOf(["feature", "enhancement", "bug", "technical"]),
  
  // Estimativas
  storyPoints: yup
    .number()
    .optional()
    .min(1, "Story points devem ser pelo menos 1")
    .max(21, "Story points devem ser no máximo 21"),
  estimatedHours: yup
    .number()
    .optional()
    .min(1, "Horas estimadas devem ser pelo menos 1")
    .max(160, "Horas estimadas devem ser no máximo 160"),
  
  // Relacionamentos
  epic: yup.string().optional(),
  milestoneId: yup.string().optional(),
  dependencies: yup.array().of(yup.string().required()).default([]),
  
  // Critérios de aceitação
  acceptanceCriteria: yup
    .array()
    .of(acceptanceCriteriaSchema)
    .required("Pelo menos um critério de aceitação é obrigatório")
    .min(1, "Deve haver pelo menos um critério de aceitação"),
  
  // Metadados
  createdAt: yup.string().required(),
  updatedAt: yup.string().required(),
  
  // Notas adicionais
  notes: yup.string().optional(),
  tags: yup.array().of(yup.string().required()).default([]),
});

// Schema para épico
export const epicSchema = yup.object({
  id: yup.string().required(),
  title: yup
    .string()
    .required("Título do épico é obrigatório")
    .min(3, "Título deve ter pelo menos 3 caracteres")
    .max(80, "Título deve ter no máximo 80 caracteres"),
  description: yup
    .string()
    .required("Descrição do épico é obrigatória")
    .min(10, "Descrição deve ter pelo menos 10 caracteres")
    .max(300, "Descrição deve ter no máximo 300 caracteres"),
  color: yup
    .string()
    .required("Cor é obrigatória")
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Cor deve ser um hex válido"),
  priority: yup
    .string()
    .required("Prioridade é obrigatória")
    .oneOf(["must_have", "should_have", "could_have", "wont_have"]),
  estimatedStoryPoints: yup.number().optional().min(1),
  stories: yup.array().of(yup.string().required()).default([]),
});

// Schema para conjunto completo de stories
export const projectStoriesSchema = yup.object({
  epics: yup.array().of(epicSchema).required().default([]),
  stories: yup
    .array()
    .of(userStorySchema)
    .required("Pelo menos uma user story é obrigatória")
    .min(1, "Deve haver pelo menos uma user story"),
  totalStoryPoints: yup.number().required().min(0),
  totalEstimatedHours: yup.number().required().min(0),
  createdAt: yup.string().required(),
  updatedAt: yup.string().required(),
});

// Schema para geração de user stories com IA
export const generateUserStoriesSchema = yup.object({
  projectId: yup.string().required("ID do projeto é obrigatório"),
  projectDescription: yup
    .string()
    .required("Descrição do projeto é obrigatória")
    .min(50, "Descrição deve ter pelo menos 50 caracteres"),
  objectives: yup
    .array()
    .of(yup.string().required())
    .required("Objetivos são obrigatórios")
    .min(1, "Deve haver pelo menos um objetivo"),
  technicalSolution: yup.object().optional(),
  timeline: yup.object().optional(),
  includeEpics: yup.boolean().default(true),
  focusAreas: yup.array().of(yup.string().required()).default([]),
});

// Tipos inferidos dos schemas para User Stories
export type AcceptanceCriteriaFormData = yup.InferType<typeof acceptanceCriteriaSchema>;
export type UserStoryFormData = yup.InferType<typeof userStorySchema>;
export type EpicFormData = yup.InferType<typeof epicSchema>;
export type ProjectStoriesFormData = yup.InferType<typeof projectStoriesSchema>;
export type GenerateUserStoriesFormData = yup.InferType<typeof generateUserStoriesSchema>;
